import Image from 'next/image';
import { memo, type ReactNode, useCallback } from 'react';

import { Button } from '@air/primitive-button';
import { Tooltip } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { usePublicCustomColors } from '~/hooks/usePublicCustomColors';
import { usePublicCustomLogo } from '~/hooks/usePublicCustomLogo';

export type PublicWorkspaceHeaderProps = {
  Search?: ReactNode;
  /**
   * When set, the header's inner content (logo/workspace identity, Search, actions) is wrapped in a
   * width-constraining box carrying these classes while the header background stays full-bleed —
   * the mechanism PublicShowcaseWorkspaceHeader uses to align its content with the collection's
   * pageWidth column. When absent, the sections render as direct children of the <header> exactly
   * as they always have (no wrapper element), so existing consumers' markup stays byte-identical.
   */
  contentClassName?: string;
  headerActions?: ReactNode;
  onWorkspaceNavigate?: () => void;
};

// Public workspace header that matches private WorkspaceHeader structure
export const PublicWorkspaceHeader = memo(
  ({ contentClassName, Search, headerActions, onWorkspaceNavigate }: PublicWorkspaceHeaderProps) => {
    const { workspaceImage, workspaceName } = usePublicWorkspace();
    const { customLogoUrl } = usePublicCustomLogo();
    const { headerBgColor, headerForegroundColor } = usePublicCustomColors();

    const getWorkspaceHeader = useCallback(() => {
      const content = (
        <div className="relative z-30 flex items-center gap-1 px-4" data-testid="PUBLIC_WORKSPACE_HEADER_INFO">
          {customLogoUrl ? (
            // No gray backing box, border, or corner rounding here: custom brand marks are often
            // transparent PNGs and frequently wide (horizontal wordmarks), so the logo renders whole
            // at a fixed height with auto width instead of cropped into the square avatar box (the
            // workspace-avatar branch below keeps the square plate as a placeholder surface).
            // max-w brackets are proto-fidelity values (mattq/proto-presentation-mode branded
            // header) — no configured max-w steps exist near them. `unoptimized` skips the
            // next/image resizer, whose fixed-size renditions would soften wide marks scaled by CSS;
            // height/width are only the intrinsic-ratio hint.
            <Image
              alt={`Logo for ${workspaceName}`}
              className="h-10 w-auto max-w-[180px] shrink-0 object-contain object-left sm:max-w-[280px]"
              height={40}
              src={customLogoUrl}
              unoptimized
              width={240}
            />
          ) : (
            <>
              <div className="relative size-10 shrink-0 overflow-hidden rounded-8 border ads:border-ads-gray-5 border-gray-5 ads:bg-ads-gray-5 bg-gray-5">
                {!!workspaceImage && (
                  <Image
                    alt={`Logo for ${workspaceName}`}
                    className="object-cover"
                    fill
                    sizes="80px"
                    src={workspaceImage}
                  />
                )}
              </div>
              <div className="flex items-center gap-1">
                <span
                  className="truncate font-semibold ads:text-secondary text-16 text-gray-11"
                  style={headerForegroundColor ? { color: headerForegroundColor } : undefined}
                >
                  {workspaceName}
                </span>
              </div>
            </>
          )}
        </div>
      );

      if (onWorkspaceNavigate) {
        return (
          <Tooltip label="View in workspace">
            <Button
              className="flex cursor-pointer items-center justify-start hover:bg-transparent"
              appearance="ghost"
              onClick={onWorkspaceNavigate}
            >
              {content}
            </Button>
          </Tooltip>
        );
      }

      // Mirror the ghost Button's box (h-8 px-2.5) so when the async workspace-member check resolves
      // and this wrapper swaps to the navigable Button, the logo doesn't shift horizontally.
      return <div className="flex h-8 items-center justify-start px-2.5">{content}</div>;
    }, [customLogoUrl, headerForegroundColor, onWorkspaceNavigate, workspaceImage, workspaceName]);

    const headerContent = (
      <>
        <div className="flex min-w-min flex-1 justify-start">{getWorkspaceHeader()}</div>

        <div className="flex justify-center px-4">
          {/* The width needs to be set here otherwise the child element will shrink to the smallest possible width */}
          <div className="w-[600px]">{Search}</div>
        </div>

        <div className="flex min-w-min flex-1 justify-end gap-2">
          {/* The padding needs to be on the child element otherwise it'll unalign the center div */}
          <div className="flex gap-2 px-4">{headerActions}</div>
        </div>
      </>
    );

    return (
      <header
        className="flex h-18 shrink-0 items-center justify-center"
        data-testid="PUBLIC_WORKSPACE_HEADER"
        style={headerBgColor ? { backgroundColor: headerBgColor } : undefined}
      >
        {contentClassName ? (
          // The content wrapper re-creates the header's flex row inside the constrained box so the
          // three flex-1/center sections keep their proportions; `justify-center` on the full-width
          // <header> centers the box while the header background stays full-bleed (same layout as
          // PublicShowcaseWorkspaceHeader's column-constrained content).
          <div
            className={cn('flex w-full items-center', contentClassName)}
            data-testid="PUBLIC_WORKSPACE_HEADER_CONTENT"
          >
            {headerContent}
          </div>
        ) : (
          headerContent
        )}
      </header>
    );
  },
);

PublicWorkspaceHeader.displayName = 'PublicWorkspaceHeader';
